/*
 * @Author: yidan.chen
 * @Date: 2025-02-10 15:55:32
 * @Last Modified by: yidan.chen
 * @Last Modified time: 2025-05-14 10:31:41
 */
import { commonServerInstance, downloadInstance } from '@/utilities/instances'
import { downloadFile } from './common'

export interface IRateCard {
  partner_id: string
  rate_card_id: string
  zone_version: string
  price_version: string
  temu_channel_code: string
  temu_inject_warehouse: boolean
  per_page: string
  page: string
}

export const createZone: (data: any) => Promise<any> = async (data: any) => {
  try {
    const response = await commonServerInstance.post(
      '/api/v1/rate-card/zones',
      data,
      {
        timeout: 0
      }
    )
    return response?.data?.data
  } catch (error) {
    console.error('Create Zone Error:', error)
    throw error
  }
}

export const fetchZoneVersions: () => Promise<
  Array<{ version: number; description: string }> | undefined
> = async () => {
  try {
    const response = await commonServerInstance.get('/api/v1/rate-card/zones')
    return response?.data?.data
  } catch (error) {
    console.error('Fetch Zone Versions Error:', error)
    throw error
  }
}

export const fetchCreators: () => Promise<any> = async () => {
  try {
    const response = await commonServerInstance.get(
      'api/v2/rate-card/partner-configs/creator-options'
    )
    return response?.data?.data
  } catch (error) {
    console.error('Fetch Creators Error:', error)
    throw error
  }
}

export const fetchTypeMatchCommonZone: () => Promise<any> = async () => {
  try {
    const response = await commonServerInstance.get(
      '/api/v1/rate-card/common-zone/list'
    )
    return response?.data?.data
  } catch (error) {
    console.error('Fetch Type Match Common Zone Error:', error)
    throw error
  }
}
export const fetchPartners: () => Promise<any> = async () => {
  try {
    return await commonServerInstance.get(
      '/api/v1/rate-card/partner-configs/partners'
    )
  } catch (error) {
    console.error('Fetch Partners Error:', error)
    throw error
  }
}

export const fetchScopedPartners: () => Promise<any> = async () => {
  try {
    const response = await commonServerInstance.get(
      '/api/v1/rate-card/partners'
    )
    return response?.data?.data?.partners
  } catch (error) {
    console.error('Fetch Scoped Partners Error:', error)
    throw error
  }
}

/**
 * The customers a partner owns, for the rate-card customer selectors.
 *
 * Scoped to one partner by the server, which is why `partner_id` is required
 * rather than optional: this is NOT `src/service/common.ts#fetchCustomers`,
 * which targets `/settle/customers` — a different module whose partner filter is
 * skipped entirely for a blank id and which performs no scope check.
 *
 * A 200 carrying `[]` means the partner genuinely has no customers. Every other
 * outcome rejects with a `NetworkError`; callers must present the two
 * differently.
 */
export const fetchRateCardCustomers = async (params: {
  partner_id: string | number
}) => {
  try {
    const response = await commonServerInstance.get(
      '/api/v2/rate-card/partner-configs/customers',
      { params }
    )
    return response?.data?.data
  } catch (error) {
    console.error('Fetch Rate Card Customers Error:', error)
    throw error
  }
}

export const fetchRateCards: () => Promise<
  Array<{ id: string; description: string }> | undefined
> = async () => {
  try {
    const response = await commonServerInstance.get(
      '/api/v1/rate-card/common-rate-cards'
    )
    return response?.data?.data
  } catch (error) {
    console.error('Fetch Rate Cards Error:', error)
    throw error
  }
}

export const createCommonZone: (data: any) => Promise<any> = async (
  data: any
) => {
  try {
    const response = await commonServerInstance.post(
      'api/v1/rate-card/common-zone/submit',
      data
    )
    return response?.data?.data
  } catch (error) {
    console.error('Create Common Zone Error:', error)
    throw error
  }
}

export const searchRateCards: (data: any) => Promise<any> = async (
  data: any
) => {
  try {
    const response = await commonServerInstance.post(
      '/api/v2/rate-card/partner-configs/search',
      data
    )
    return response?.data?.data
  } catch (error) {
    console.error('Search Rate Cards Error:', error)
    throw error
  }
}

export const downloadZone = async (data: any) => {
  try {
    const response = await downloadInstance.get(
      `/api/v1/rate-card/zones/${data.zoneVersionId}`,
      {
        responseType: 'blob'
      }
    )

    // No client filename: downloadFile reads Content-Disposition
    // (`zone_details_version_{version}_{Ymd_His}.csv`).
    return await downloadFile(response)
  } catch (error) {
    console.error('Download error:', error)
    throw error
  }
}

export const downloadPrice = async (data: any) => {
  try {
    const response = await downloadInstance.get(
      `/api/v1/rate-card/prices/${data.priceVersionId}`,
      {
        responseType: 'blob'
      }
    )

    // No client filename: downloadFile reads Content-Disposition
    // (`price_details_version_{version}_{Ymd_His}.csv`).
    return await downloadFile(response)
  } catch (error) {
    console.error('Download error:', error)
    throw error
  }
}

/**
 * The card-keyed price download — Story 4.2.
 *
 * Keyed off the card's own `id`, never off `price_version`: search rows and the
 * detail payload both carry `id`, for an approved numeric card and a pending
 * `temp_*` one alike, so the list action and the detail action make the
 * identical call with no branching.
 *
 * `downloadFile` is called with **no filename argument**, and that omission is
 * the whole point. The header is consulted only when no filename is passed, so
 * supplying one here would discard the server's
 * `price_details_card_{id}_p{partner}_c{customerId|all}_{ground|air}_v{version}_{ts}.csv`
 * — the only thing that keeps a ground and an air download for the same partner
 * from overwriting each other. Do not add a "safety" fallback name;
 * `downloadFile` already falls back to `download.csv` internally.
 *
 * Rethrows so the call sites can raise the snackbar: the server's 404 ("No
 * price detail available for this rate card") and its 403 scope refusal both
 * arrive with `.message` already unwrapped by `downloadInstance`'s interceptor.
 */
export const downloadCardPrice = async (data: { id: string | number }) => {
  try {
    const response = await downloadInstance.get(
      `/api/v2/rate-card/partner-configs/${data.id}/download-price`,
      {
        responseType: 'blob'
      }
    )

    return await downloadFile(response)
  } catch (error) {
    console.error('Download error:', error)
    throw error
  }
}

export const fetchRateCardDetail: (id: string) => Promise<any> = async (
  id: string
) => {
  try {
    const response = await commonServerInstance.get(
      `api/v2/rate-card/partner-configs/${id}`
    )
    return response?.data?.data
  } catch (error) {
    console.error('Fetch Rate Card Detail Error:', error)
    throw error
  }
}

export const submitRateCard = async (
  params:
    | FormData
    | {
        partner_id: string
        beg_date: string
        end_date: string
        note?: string
        surcharge?: Record<string, string>
      }
) => {
  try {
    const response = await commonServerInstance.post(
      '/api/v2/rate-card/partner-configs/submit',
      params,
      params instanceof FormData
        ? {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        : undefined
    )
    return response?.data?.data
  } catch (error) {
    console.error('Submit Rate Card Error:', error)
    throw error
  }
}

export const updateRateCardNote = async (id: string, note: string) => {
  try {
    const response = await commonServerInstance.put(
      `/api/v2/rate-card/partner-configs/${id}/note`,
      { note }
    )
    return response?.data?.data
  } catch (error) {
    console.error('Update Rate Card Note Error:', error)
    throw error
  }
}
